import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ReportParams } from "./useReportParams";
import type { ReportTimeframeKey } from "./computePrevWindow";

/**
 * Lets a report CARD be mounted outside the report page and driven by a
 * different filter bar — today, the dashboard's.
 *
 * `useReportParams` normally derives everything from `reportsV2` Redux state.
 * A card rendered on the dashboard must NOT read that: the two surfaces have
 * separate filter bars and the user's requirement is that they stay separate
 * (directive 2026-08-17 — "don't interconnect both reports and dashboard
 * filter on this widget"). Changing the dashboard's timeframe must not move a
 * report, and vice versa.
 *
 * So the params become an injectable seam. Wrap a card in this provider and
 * every `useReportParams()` inside it — including the ones nested in shared
 * hooks like `useOpenLeads`, which is why a prop would not have been enough —
 * returns the host surface's params instead.
 *
 * The card's own UI state needs the same separation, and gets it from
 * `<WidgetScope>` (see `state/useWidgetState.ts`): the dashboard copy is
 * mounted under its own scope id, so its pivot / search / selection live under
 * `dashboard.*` keys and can't collide with the report copy's.
 */

export interface ReportParamsOverride extends ReportParams {
  /**
   * The host's branch / role / user selections, for the few hooks that read
   * them directly off the filter bar rather than through params (the
   * cascade-aware option lists). "all" = unset, matching the report bar.
   */
  scope?: { branch?: string; role?: string; user?: string };
  /**
   * The host's date pick, so the window LABEL a card prints ("This month")
   * names the window its numbers ran on. `useReportTimeframe` reads it; the
   * scope selectors read `scope` the same way.
   */
  timeframe?: ReportTimeframeKey;
}

const Ctx = createContext<ReportParamsOverride | null>(null);

export function ReportParamsOverrideProvider({
  value,
  children,
}: {
  value: ReportParamsOverride;
  children: ReactNode;
}) {
  // Memoized on the params identity so a host re-render doesn't churn every
  // query key beneath it.
  const stable = useMemo(
    () => value,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(value.params),
      JSON.stringify(value.prevParams),
      value.isCompare,
      JSON.stringify(value.scope),
      value.timeframe,
    ],
  );
  return <Ctx.Provider value={stable}>{children}</Ctx.Provider>;
}

/** null on the report page (the normal case) — see the provider's note. */
export function useReportParamsOverride(): ReportParamsOverride | null {
  return useContext(Ctx);
}
